import { useCallback, useEffect, useState } from 'react'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { GripVertical, ChevronLeft, ChevronRight } from 'lucide-react'
import { useFeature } from '@/hooks/useFeature'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'
import { usePersonaBundle, personaBundleQueryKey } from '@/hooks/usePersonaBundle'
import { usePersonas, type PersonaEdit } from '@/hooks/usePersonas'
import { useUpdatePersonaBundle } from '@/hooks/useUpdatePersonaBundle'
import { personaReviewKey } from '@/hooks/usePersonaReview'
import { personaBundleHistoryKey } from '@/hooks/usePersonaBundleHistory'
import { useBundleFindings } from '@/hooks/useBundleFindings'
import { useResizablePanel } from '@/hooks/useResizablePanel'
import { summarizeFindings } from '@/utils/findingsSummary'
import { useComposerStore, registerComposerMutation } from '@/stores/composerStore'
import type { Finding, FindingLocation } from '@/utils/bundleFindings'
import type { PersonaBundleResponse } from '@/types/personaWizard'
import type { ApiPersona } from '@/types/persona'
import { PersonaEditor } from '@/components/config'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '@/components/ui/Modal'
import { WorkshopModeProvider } from './WorkshopModeContext'
import { WorkshopTopBar } from './WorkshopTopBar'
import { IdentitySection } from './IdentitySection'
import { BlockSection } from './BlockSection'
import { CapabilitiesSection } from './CapabilitiesSection'
import { AdvancedSection } from './AdvancedSection'
import { CoachRail } from './CoachRail'
import { BlockFocusEditor } from './BlockFocusEditor'
import { AddBlockGallery } from './AddBlockGallery'
import { ComposedPromptInspector } from './ComposedPromptInspector'
import { VersionHistoryDrawer } from './VersionHistoryDrawer'
import { FocusedBlockProvider, useFocusedBlock } from './FocusedBlockContext'
import { BLOCK_TAXONOMY } from './blockTaxonomy'
import { workshopCopy } from './workshopCopy'
import { usePersonaBasicsDraft, type PersonaBasicsDraft } from './usePersonaBasicsDraft'
import { workshopAnchorId } from './workshopAnchors'


export function PersonaWorkshop() {
  const { personaId } = useParams<{ personaId: string }>()
  const enabled = useFeature('persona_workshop')
  const query = usePersonaBundle(personaId ?? null)

  if (!enabled) return <Navigate to="/personas" replace />
  if (query.isLoading) return <div className="p-6" data-testid="workshop-loading">Loading…</div>
  if (query.isError || !query.data) return <div className="p-6">Couldn't load this persona.</div>
  if (!query.data.bundle) {
    // Should-not-happen since v2→core: bundle at birth + the boot
    // backfill guarantee a current bundle. Not a legacy mode — an error.
    return (
      <div className="p-6">
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm">
          This persona has no structured bundle — contact support.
        </div>
      </div>
    )
  }

  // key remounts the inner content (and re-seeds the basics draft) when the
  // persona changes.
  return (
    <WorkshopContent
      key={query.data.persona.id}
      persona={query.data.persona}
      bundle={query.data.bundle}
    />
  )
}

function WorkshopContent({ persona, bundle }: { persona: ApiPersona; bundle: PersonaBundleResponse }) {
  const load = useComposerStore((s) => s.load)
  const reset = useComposerStore((s) => s.reset)
  const commitDraft = useComposerStore((s) => s.commitDraft)
  const discardDraft = useComposerStore((s) => s.discardDraft)
  const dirtyCount = useComposerStore((s) => s.dirtyPaths.size)
  const serverBundle = useComposerStore((s) => s.serverBundle)
  const updateMutation = useUpdatePersonaBundle()
  const findings = useBundleFindings()
  const summary = summarizeFindings(findings)
  const basics = usePersonaBasicsDraft(persona)
  // Real usePersonas returns personas as Persona[] (array), not a record keyed by id.
  const { personas, fetchPersonas, updatePersona } = usePersonas()
  const camelPersona = Array.isArray(personas)
    ? personas.find((p) => p.id === persona.id)
    : undefined
  const queryClient = useQueryClient()
  const [modalTab, setModalTab] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [inspectorOpen, setInspectorOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const { size: railWidth, isDragging, handleMouseDown } = useResizablePanel({
    defaultSize: 360,
    minSize: 280,
    maxSize: 560,
    storageKey: 'workshop-coach-rail-width',
    side: 'right',
  })
  const [railHidden, setRailHidden] = useState<boolean>(
    () => localStorage.getItem('workshop-coach-rail-hidden') === 'true',
  )
  useEffect(() => {
    localStorage.setItem('workshop-coach-rail-hidden', String(railHidden))
  }, [railHidden])

  // Spec D5: a wizard publish lands here via `?rail=review` so CoachRail
  // opens on the Review tab (review itself is NOT auto-run — "Review now"
  // is the call to action). Captured once on mount; invalid values are
  // ignored (CoachRail's own 'preview' default applies).
  const [searchParams, setSearchParams] = useSearchParams()
  const [initialRail] = useState<'coach' | 'preview' | 'review' | null>(() => {
    const r = searchParams.get('rail')
    return r === 'coach' || r === 'preview' || r === 'review' ? r : null
  })
  useEffect(() => {
    if (searchParams.has('rail')) {
      const next = new URLSearchParams(searchParams)
      next.delete('rail')
      setSearchParams(next, { replace: true })
    }
    // Consume-once on mount: re-running this on every searchParams change
    // would bounce a user-selected tab back to `rail`'s value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const bundleId = bundle?.id ?? null
  useEffect(() => {
    load(persona, bundle)
    return () => reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- key on stable ids so a same-id background refetch can't stomp the in-flight draft
  }, [persona.id, bundleId, load, reset])
  useEffect(() => {
    registerComposerMutation(updateMutation.mutateAsync)
    return () => registerComposerMutation(null)
  }, [updateMutation.mutateAsync])
  // Ensure the camelCase persona is available for the launched modal.
  useEffect(() => { if (!camelPersona) void fetchPersonas() }, [camelPersona, fetchPersonas])

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      if (basics.isDirty) await basics.save()
      if (dirtyCount > 0) {
        await commitDraft()
        // A Save mints a new bundle id, which dates every cached review:
        // its findings address block INDICES into the bundle it ran
        // against. Refetching re-reads current_bundle_id and the
        // server-computed staleness flags so the Review tab stops
        // claiming to be current. The GET runs no LLM, so this is free.
        void queryClient.invalidateQueries({
          queryKey: personaReviewKey(persona.id),
        })
        // A Save also mints a new version — the history drawer's list must
        // refetch so the new row appears (and its diffs stay accurate) next
        // time it's opened.
        void queryClient.invalidateQueries({
          queryKey: personaBundleHistoryKey(persona.id),
        })
      }
    } finally {
      setSaving(false)
    }
  }, [basics, dirtyCount, commitDraft, queryClient, persona.id])

  const handleRestore = useCallback(
    async (version: PersonaBundleResponse) => {
      if (!serverBundle) return
      const activated = await updateMutation.mutateAsync({
        bundleId: serverBundle.id,
        updates: {
          blocks: version.blocks,
          default_skills: version.default_skills,
          auto_skills: version.auto_skills,
          tool_bindings: version.tool_bindings,
          workflow_bindings: version.workflow_bindings,
          channel_overrides: version.channel_overrides,
          policy: version.policy,
          variable_values: version.variable_values,
        },
      })
      // serverBundle := activated, draft/dirty cleared. A failed mutation
      // throws before this line, so the user's draft survives failure.
      load(persona, activated)
      // Same invalidation contract as handleSave — a restore mints a new
      // bundle id, which dates every cached review.
      void queryClient.invalidateQueries({ queryKey: personaReviewKey(persona.id) })
      void queryClient.invalidateQueries({ queryKey: personaBundleHistoryKey(persona.id) })
    },
    [serverBundle, updateMutation, load, persona, queryClient],
  )

  // Task 14: translate the Composer-era card vocabulary a wiring finding's
  // FindingLocation still carries into a Workshop section anchor, then
  // scroll it into view and ring-flash it for ~1s. workshopAnchorId returns
  // null for anything unrecognised — deliberately no fallback target, since
  // a jump to the wrong place is worse than no jump at all.
  const handleJump = useCallback((loc: FindingLocation) => {
    const anchor = workshopAnchorId(loc)
    if (!anchor) return
    const el = document.getElementById(anchor)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    el.setAttribute('data-flash', 'true')
    window.setTimeout(() => el.removeAttribute('data-flash'), 1000)
  }, [])

  const dirty = basics.isDirty || dirtyCount > 0

  // Navigate-away guard (spec c): beforeunload always; in-app blocking when a
  // data router is present (main.tsx provides one). Blocked navigation opens
  // the confirm Modal rendered below.
  const guard = useUnsavedChangesGuard({ when: dirty && !saving })
  const [leaveError, setLeaveError] = useState<string | null>(null)
  const handleStay = () => {
    setLeaveError(null)
    guard.reset()
  }
  const handleSaveAndLeave = async () => {
    setLeaveError(null)
    try {
      await handleSave()
      guard.proceed()
    } catch (err) {
      setLeaveError(err instanceof Error ? err.message : 'Save failed (unknown error)')
    }
  }
  const handleDiscardAndLeave = () => {
    setLeaveError(null)
    discardDraft()
    basics.reset()
    guard.proceed()
  }

  const handleModalSave = useCallback(
    async (data: PersonaEdit) => {
      // PersonaEditor delegates persistence to this onSave prop (it does NOT
      // save internally — it calls onSave, then onOpenChange(false)). Mirror
      // PersonasPage.handleSavePersona: write the edit to the DB via
      // updatePersona (PUT /api/personas/:id). The modal's onOpenChange then
      // invalidates the bundle query so the refetch reflects the saved change.
      await updatePersona(persona.id, data)
    },
    [persona.id, updatePersona],
  )

  return (
    <WorkshopModeProvider>
      <div id="workshop-top" className="mx-auto max-w-6xl">
        {/* Bleed over <main>'s padding (MarketingPage idiom) so no content
            strip scrolls visibly above the pinned bar. */}
        <div className="sticky -top-4 lg:-top-6 z-20 -mx-4 -mt-4 px-4 pt-4 lg:-mx-6 lg:-mt-6 lg:px-6 lg:pt-6 bg-background">
          <WorkshopTopBar
            personaName={persona.name}
            summary={summary}
            dirty={dirty}
            onSave={handleSave}
            saving={saving}
            avatarUrl={camelPersona?.avatarUrl ?? null}
            onViewPrompt={() => setInspectorOpen(true)}
            onViewHistory={() => setHistoryOpen(true)}
            version={serverBundle?.version}
          />
        </div>
        {/* FocusedBlockProvider spans BOTH columns: WorkshopBody needs it for
            the gallery/focus editor, and the rail's ReviewPanel calls
            useFocusedBlock() to open blocks from findings — scoping the
            provider to WorkshopBody alone crashes the Review tab. */}
        <FocusedBlockProvider personaId={persona.id}>
          <div className="flex flex-col gap-4 p-4 lg:flex-row">
            <WorkshopBody
              persona={persona}
              basics={basics}
              avatarUrl={camelPersona?.avatarUrl ?? null}
              findings={findings}
              onJump={handleJump}
              onLaunchModal={setModalTab}
              bundleId={serverBundle?.id ?? null}
              dirty={dirty}
              onSave={handleSave}
              saving={saving}
              inspectorOpen={inspectorOpen}
              onOpenInspector={() => setInspectorOpen(true)}
              onCloseInspector={() => setInspectorOpen(false)}
            />
          {/*
            Sticky offsets are PAIRED with the Workshop bar's height — keep in sync:
            - Scroll container is AppShell's <main> (overflow-auto). The rail is
              desktop-only (lg:flex), and from lg up the app has no top bar
              (SA-965), so <main> starts at the top of the window: the
              scrollport is 100vh tall and sticky offsets are relative to ITS
              top. (A View-as banner above <main> is not counted.)
            - The top-bar wrapper bleeds over <main>'s padding (-mt/-mx + pt/px)
              and pins with a NEGATIVE sticky top (-top-4/lg:-top-6) so it
              covers the scroll container's own padding strip (overflow paints
              scrolling content inside the padding box; a 0 offset leaves a
              24px sliver of moving content above the bar).
            - Pinned, the bar's bottom lands 57px past the scrollport top
              (pt-6 - 24px offset + WorkshopTopBar 57px: py-3 + h-8 content
              + border-b) → rail offset top-[57px], flush below the bar.
            - Height: 57px (rail offset) is derived from the classes above;
              the remaining 48px is EMPIRICAL — it absorbs <main>'s pb
              (p-4/lg:p-6) plus the content row's own bottom spacing so the
              rail lands flush with the content padding edge.
              Total: calc(100vh - 105px). If any surrounding padding changes,
              re-tune LIVE (measure rail bottom vs viewport) — do not derive
              the 48px from a single padding class.
            Live-tuned 2026-07-03 @720px: strip above bar 0px, rail gap 0px,
            rail bottom flush with the content padding edge; re-measured
            without the app bar for SA-965.
          */}
          {railHidden ? (
            <button
              type="button"
              onClick={() => setRailHidden(false)}
              title="Show coach panel"
              aria-label="Show coach panel"
              className="hidden lg:flex sticky top-[57px] h-[calc(100vh-105px)] w-6 flex-col items-center justify-center rounded-md border border-border bg-muted hover:bg-muted-foreground/10"
            >
              <ChevronLeft className="h-4 w-4 text-muted-foreground" />
            </button>
          ) : (
            <div
              className="hidden lg:flex sticky top-[57px] h-[calc(100vh-105px)] shrink-0"
              style={{ width: railWidth }}
            >
              {/* drag handle on the rail's left edge */}
              <div
                role="separator"
                aria-orientation="vertical"
                onMouseDown={handleMouseDown}
                className={`flex w-2 shrink-0 cursor-col-resize items-center justify-center rounded-sm hover:bg-muted-foreground/10 ${isDragging ? 'bg-muted' : ''}`}
              >
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setRailHidden(true)}
                    title="Hide coach panel"
                    aria-label="Hide coach panel"
                    className="rounded-sm p-1 text-muted-foreground hover:bg-muted-foreground/10"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
                <div className="min-h-0 flex-1">
                  <CoachRail
                    personaId={persona.id}
                    bundleId={serverBundle?.id ?? null}
                    hasUnsavedChanges={dirty}
                    findings={findings}
                    onJump={handleJump}
                    avatarUrl={camelPersona?.avatarUrl ?? null}
                    personaName={persona.name}
                    defaultTab={initialRail ?? 'preview'}
                  />
                </div>
              </div>
            </div>
          )}
          </div>
        </FocusedBlockProvider>
      </div>
      {modalTab !== null && camelPersona && (
        <PersonaEditor
          open
          onOpenChange={(o) => {
            if (!o) {
              setModalTab(null)
              // Modal may have edited rag_config (model/params); refetch so dependent
              // views aren't stale until refocus.
              void queryClient.invalidateQueries({ queryKey: personaBundleQueryKey(persona.id) })
            }
          }}
          initialTab={modalTab}
          persona={camelPersona}
          onSave={handleModalSave}
        />
      )}
      {guard.element}
      <Modal open={guard.blocked} onOpenChange={(o) => { if (!o) handleStay() }}>
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle>Leave without saving?</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p className="text-sm text-muted-foreground">
              You have unsaved changes. Save them before you leave, or discard them and go.
            </p>
            {leaveError && (
              <Alert variant="destructive" className="mt-3 text-xs">
                {leaveError}
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={handleStay}>
              Stay
            </Button>
            <Button variant="ghost" onClick={handleDiscardAndLeave}>
              Discard &amp; leave
            </Button>
            <Button variant="default" isLoading={saving} onClick={() => void handleSaveAndLeave()}>
              Save &amp; leave
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <VersionHistoryDrawer
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        personaId={persona.id}
        dirty={dirtyCount > 0}
        onRestore={handleRestore}
      />
    </WorkshopModeProvider>
  )
}

interface WorkshopBodyProps {
  persona: ApiPersona
  basics: PersonaBasicsDraft
  avatarUrl: string | null
  findings: Finding[]
  onJump: (loc: FindingLocation) => void
  onLaunchModal: (tab: string) => void
  bundleId: string | null
  dirty: boolean
  /** Top-bar save handler — the focus editor's footer Save invokes the SAME one. */
  onSave: () => Promise<void>
  saving: boolean
  inspectorOpen: boolean
  onOpenInspector: () => void
  onCloseInspector: () => void
}

/**
 * Content column + focus editor + Add-block gallery, rendered inside
 * FocusedBlockProvider so the gallery's onPick can call useFocusedBlock().open()
 * after appending the new block (Task 6). Every BlockSection's "+ Add block"
 * opens the SAME gallery instance — the picked category (not the section it
 * was opened from) decides where the new block ultimately renders.
 */
function WorkshopBody({
  persona,
  basics,
  avatarUrl,
  findings,
  onJump,
  onLaunchModal,
  bundleId,
  dirty,
  onSave,
  saving,
  inspectorOpen,
  onOpenInspector,
  onCloseInspector,
}: WorkshopBodyProps) {
  const copy = workshopCopy(persona.name)
  const addBlock = useComposerStore((s) => s.addBlock)
  const base = useComposerStore((s) => s.draftBundle ?? s.serverBundle)
  const { open, requestReveal } = useFocusedBlock()
  const [galleryOpen, setGalleryOpen] = useState(false)

  const handlePick = (category: string) => {
    // Read length/ordinal from the pre-add bundle — addBlock appends, so the
    // new block's absolute index equals the current blocks.length, and its
    // ordinal equals how many same-category blocks already exist.
    const index = base?.blocks.length ?? 0
    const ordinal = base?.blocks.filter((b) => b.category === category).length ?? 0
    // The new card renders in its category's destination section (which may not
    // be where + Add block was clicked). Queue a reveal so that section expands
    // and the card is scrolled-to + flashed once the focus editor closes.
    // Unknown/custom categories fall into the 'extra' (Formatting & extras) group.
    const group = (BLOCK_TAXONOMY as Record<string, { group: string }>)[category]?.group ?? 'extra'
    addBlock(category)
    requestReveal({ group, category, ordinal })
    open({ index, category, ordinal })
  }

  return (
    <>
      <div className="min-w-0 flex-1 space-y-3">
        <IdentitySection persona={persona} basics={basics} avatarUrl={avatarUrl} />
        <BlockSection group="how" emoji="🎨" title={copy.howTitle} subtitle="Personality, tone of voice, and behavior." name={persona.name} starters={['trait']} onAddBlock={() => setGalleryOpen(true)} />
        <CapabilitiesSection persona={persona} onJump={onJump} />
        <BlockSection group="rules" emoji="🛡️" title={copy.rulesTitle} subtitle="Hard limits, must-remembers, and when to escalate." name={persona.name} onAddBlock={() => setGalleryOpen(true)} />
        <BlockSection group="know" emoji="📚" title={copy.knowTitle} subtitle="Reference facts and sample do / don’t messages." name={persona.name} onAddBlock={() => setGalleryOpen(true)} />
        <BlockSection group="extra" emoji="🧩" title={copy.extraTitle} subtitle="Output shape, custom blocks, anything else." name={persona.name} onAddBlock={() => setGalleryOpen(true)} />
        <AdvancedSection onLaunchModal={onLaunchModal} onViewComposedPrompt={onOpenInspector} />
      </div>
      <BlockFocusEditor
        personaName={persona.name}
        personaId={persona.id}
        bundleId={bundleId}
        hasUnsavedChanges={dirty}
        onSave={onSave}
        saving={saving}
        findings={findings}
        onJump={onJump}
        avatarUrl={avatarUrl}
      />
      <AddBlockGallery open={galleryOpen} onClose={() => setGalleryOpen(false)} onPick={handlePick} />
      <ComposedPromptInspector
        open={inspectorOpen}
        onClose={onCloseInspector}
        personaId={persona.id}
        bundleId={bundleId}
        dirty={dirty}
      />
    </>
  )
}

export default PersonaWorkshop
